import { useMemo, useState, type ClipboardEvent } from 'react';
import { format } from 'date-fns';
import { Loader2, Sprout } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { DateRangeSelector } from '../../statistics/components/DateRangeSelector';
import { ExportMenu, FORMAT_DOCX, FORMAT_GDOC } from '../../statistics/components/ExportMenu';
import { periodDays } from '../../statistics/lib/date-range';
import type { DateRangeOption } from '../../statistics/types/account-stats';
import { Textarea } from '../../../components/ui/textarea';
import { fetchPresentationMedia } from '../../reports/api/presentation-media';
import { fetchReportImageUrls } from '../../statistics/api/account-report';
import { exactRu, plural } from '../../statistics/lib/format-metrics-ru';
import { fetchTikTokLinkStats } from '../../statistics/api/tiktok-link-stats';
import { fetchMediaComments, fetchTikTokComments } from '../api/media-comments';
import { attachSeedingComments } from '../lib/attach-comments';
import { attachSeedingCovers } from '../lib/attach-covers';
import {
  buildSeedingReport,
  isSeedingLink,
  seedingPeriod,
  type BuildSeedingReportResult,
} from '../lib/build-seeding-report';
import { drawCards, generateSeedingDocx } from '../lib/export-seeding-docx';
import { generateSeedingGoogleDoc } from '../lib/export-seeding-gdoc';
import { cleanPastedLinks } from '../lib/clean-pasted-links';
import { loadCommentImages, type CommentImages } from '../lib/comment-images';
import { parseSeedingInput } from '../lib/parse-seeding-input';
import { makeCommentFilter } from '../lib/screen-comments';

// В образце отчёта — «Топ 10 видео».
const DEFAULT_TOP = 10;
const MAX_TOP = 50;
// В образце под роликом от одного до трёх скриншотов с комментариями.
const COMMENTS_PER_GROUP = 3;

/** Перезаливка обложек: id публикации → постоянный адрес. */
async function fetchCoverUrls(items: Array<{ id: string; thumbnailUrl: string }>): Promise<Map<string, string>> {
  const urls = new Map<string, string>();
  await fetchReportImageUrls(items, {
    onResult: (r) => {
      if (r.imageUrl) urls.set(r.id, r.imageUrl);
    },
    onProgress: () => {},
  });
  return urls;
}

/**
 * Отчёт «Отработано»: ссылки за неделю → топ посевов в Word.
 *
 * Собирается в два шага. Статистика по паре сотен ссылок идёт минутами, и
 * оператору нужно увидеть, как разложились посевы, до того как файл уйдёт
 * заказчику: одна и та же новость у разных пабликов бывает подписана
 * по-разному, и такие копии разъедутся по двум блокам. Поэтому сначала
 * «Собрать посевы» с разбором на экране, а скачивание — отдельной кнопкой,
 * которая уже ничего не ждёт.
 */
export function SeedingReportCard(): JSX.Element {
  const [linksText, setLinksText] = useState('');
  const [topInput, setTopInput] = useState(String(DEFAULT_TOP));
  const [withComments, setWithComments] = useState(true);
  // Период в шапке необязателен: без него — по датам самих публикаций.
  const [rangeOption, setRangeOption] = useState<DateRangeOption | null>(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const [progress, setProgress] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [result, setResult] = useState<BuildSeedingReportResult | null>(null);
  const [commentIssues, setCommentIssues] = useState<string[]>([]);
  // Аватарки, эмодзи и стикеры TikTok догружаются после отбора; до тех пор — без них.
  const [images, setImages] = useState<Map<string, CommentImages>>(new Map());
  const cards = useMemo(() => drawCards(result?.top ?? [], images), [result, images]);
  const busy = isBuilding || isExporting;

  const input = useMemo(() => parseSeedingInput(linksText), [linksText]);

  // Вставка из таблицы или чата: в поле ложатся только ссылки (и темы), см. clean-pasted-links.
  const handlePaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = e.clipboardData.getData('text/plain');
    const cleaned = cleanPastedLinks(pasted);
    if (cleaned === pasted.replace(/\r\n?/g, '\n')) return;
    e.preventDefault();
    const el = e.currentTarget;
    const start = el.selectionStart;
    const next = linksText.slice(0, start) + cleaned + linksText.slice(el.selectionEnd);
    setLinksText(next);
    requestAnimationFrame(() => el.setSelectionRange(start + cleaned.length, start + cleaned.length));
  };
  const links = useMemo(() => input.blocks.flatMap((b) => b.urls), [input]);
  const validLinks = useMemo(() => links.filter(isSeedingLink), [links]);

  const topCount = Number.parseInt(topInput, 10);
  const topValid = Number.isInteger(topCount) && topCount >= 1 && topCount <= MAX_TOP;
  const canBuild = !busy && validLinks.length > 0 && topValid;

  // Период в шапке: что выбрали руками, иначе — по датам самих публикаций.
  const autoPeriod = result ? seedingPeriod(result.top) : null;
  const period = rangeOption ? periodDays(rangeOption) : autoPeriod;

  async function handleBuild(): Promise<void> {
    if (!topValid || busy) return;
    setIsBuilding(true);
    setResult(null);
    setImages(new Map());
    setCommentIssues([]);
    try {
      const built = await buildSeedingReport(
        input,
        topCount,
        { fetchMedia: fetchPresentationMedia, fetchTikTok: fetchTikTokLinkStats },
        setProgress,
      );
      setResult(built);

      const top = await attachSeedingCovers(built.top, { fetchImageUrls: fetchCoverUrls }, setProgress);
      setResult({ ...built, top });

      if (top.length > 0 && withComments) {
        const attached = await attachSeedingComments(
          top,
          { fetchComments: fetchMediaComments, fetchTikTokComments },
          {
            count: COMMENTS_PER_GROUP,
            filter: makeCommentFilter(COMMENTS_PER_GROUP, undefined, () => {
              setCommentIssues((issues) => [...new Set([...issues,
                'Проверка части комментариев не завершилась. Они не включены в отчёт. Повторите сборку.',
              ])]);
            }),
          },
          setProgress,
        );
        setResult({ ...built, top: attached.groups });
        setImages(
          await loadCommentImages(attached.groups.flatMap((g) => g.comments ?? []), { fetchImageUrls: fetchCoverUrls }),
        );
        if (attached.errors.length > 0) {
          console.warn('[Отработано] комментарии не прочитались:', attached.errors);
          setCommentIssues((issues) => [...issues, ...attached.errors.map(
            (e) => `Комментарии публикации ${e.mediaId}: ${e.error}`,
          )]);
          toast.warning(`Комментарии не прочитались у публикаций: ${attached.errors.length}.`);
        }
      }

      if (built.top.length === 0) {
        toast.error('Ни по одной ссылке не удалось получить статистику');
        return;
      }
      if (built.errors.length > 0) {
        toast.warning(`Не попало в отчёт ссылок: ${built.errors.length}. Список под разбором.`);
      }
      if (built.noStats.length > 0) {
        toast.warning(`Без статистики публикаций: ${built.noStats.length}. Суммы занижены.`);
      }
    } catch (err) {
      console.error('[Отработано] сбой сборки:', err);
      const message = err instanceof Error ? err.message : 'Не удалось собрать посевы';
      setCommentIssues((issues) => [...issues, `Сборка прервалась: ${message}. Данные могут быть неполными — повторите сборку.`]);
      toast.error(message);
    } finally {
      setIsBuilding(false);
      setProgress('');
    }
  }

  async function handleDownload(): Promise<void> {
    if (!result || result.top.length === 0 || busy) return;
    setIsExporting(true);
    try {
      await generateSeedingDocx({ groups: result.top, period, cards });
      toast.success('Отчёт сформирован');
    } catch (err) {
      console.error('[Отработано] сбой файла:', err);
      toast.error(err instanceof Error ? err.message : 'Не удалось собрать файл');
    } finally {
      setIsExporting(false);
    }
  }

  async function handleGoogleDoc(): Promise<void> {
    if (!result || result.top.length === 0 || busy) return;
    setIsExporting(true);
    try {
      const exported = await generateSeedingGoogleDoc({ groups: result.top, period, cards }, setProgress);
      if (exported.missingImages > 0) {
        const message = `Не удалось вставить изображений: ${exported.missingImages}. Комментарии сохранены текстом. Для карточек скачайте Word или повторите экспорт.`;
        setCommentIssues((issues) => [...issues, message]);
        toast.warning(message);
      } else {
        toast.success('Документ создан и открыт в новой вкладке');
      }
    } catch (err) {
      console.error('[Отработано] сбой документа:', err);
      toast.error(err instanceof Error ? err.message : 'Не удалось создать документ');
    } finally {
      setIsExporting(false);
      setProgress('');
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Отработано</CardTitle>
        <CardDescription>
          Еженедельный отчёт по ссылкам: копии одного ролика в разных пабликах складываются в один
          посев (нужна строка на каждое размещение — это «Посев в таблице»). Разложите ссылки по темам: <b>две пустые строки</b> — новая тема,{' '}
          <b>одна</b> — русская и казахская версии той же новости. Файл соберётся в вашем порядке.
          Свалили кучей — посевы соберутся сами по подписям. Ролики TikTok подключённых аккаунтов
          идут наравне с постами Instagram. В обоих случаях в файл пойдут самые
          просматриваемые, сколько указано в «Посевов в топе». Цифры складываются по всем копиям посева, под каждым — топовые
          комментарии.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="seeding-links">Ссылки на публикации</Label>
          <Textarea
            id="seeding-links"
            rows={10}
            placeholder={
              'Над Сеулом пролетел дирижабль\n' +
              'https://www.instagram.com/kazpress/reel/…\n' +
              'https://www.instagram.com/news.qaz/reel/…\n\n' +
              'https://www.instagram.com/qazaqstan.24/reel/…\n' +
              'https://www.instagram.com/alash_news/reel/…\n' +
              'https://www.tiktok.com/@toqayev_life/video/…\n\n\n' +
              'Группа «Ұлытау» исполнила «Жұмырқылышты»\n' +
              'https://www.instagram.com/news.qaz/reel/…'
            }
            value={linksText}
            onChange={(e) => setLinksText(e.target.value)}
            onPaste={handlePaste}
            disabled={busy}
          />
          <p className="text-xs text-muted-foreground">
            {input.manual
              ? `Разобрано тем: ${input.blocks.length} — пойдут в файл в этом порядке${topValid && input.blocks.length > topCount ? `, ${topCount} самых просматриваемых` : ''}. Тему можно не писать: возьмём первую фразу подписи.`
              : 'Тема — строкой над её ссылками, между темами две пустые строки. Без разметки посевы соберутся сами по подписям.'}
            {links.length > 0 &&
              ` Ссылок: ${validLinks.length}${links.length > validLinks.length ? `, не ссылок на пост или ролик: ${links.length - validLinks.length}` : ''}.`}
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <div className="space-y-2">
            <Label>Период в шапке</Label>
            <DateRangeSelector
              value={rangeOption}
              onChange={setRangeOption}
              emptyLabel="По датам публикаций"
              onClear={() => setRangeOption(null)}
              disabled={busy}
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="seeding-count">Посевов в топе</Label>
            <Input
              id="seeding-count"
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_TOP}
              value={topInput}
              onChange={(e) => setTopInput(e.target.value)}
              disabled={busy}
              aria-invalid={!topValid}
              className="sm:w-32"
            />
            {!topValid && <p className="text-xs text-destructive">От 1 до {MAX_TOP}</p>}
            {topValid && input.manual && input.blocks.length > topCount && (
              <p className="text-xs text-muted-foreground">Самые просматриваемые, в вашем порядке</p>
            )}
          </div>
        </div>

        <div className="flex items-start justify-between gap-4 rounded-md border p-3">
          <div className="space-y-0.5">
            <Label htmlFor="seeding-comments">Тянуть комментарии</Label>
            <p className="text-xs text-muted-foreground">
              До {COMMENTS_PER_GROUP} комментариев под каждым посевом, по лайкам среди прошедших
              проверку. Негатив, критику, насмешки и неоднозначные реплики исключаем.
              Перед выгрузкой просмотрите карточки: неподходящую можно убрать.
            </p>
          </div>
          <Switch
            id="seeding-comments"
            checked={withComments}
            onCheckedChange={setWithComments}
            disabled={busy}
          />
        </div>

        <Button onClick={() => void handleBuild()} disabled={!canBuild} className="w-full gap-2">
          {isBuilding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sprout className="h-4 w-4" />}
          Собрать посевы
        </Button>

        {isBuilding && progress && <p className="text-sm text-muted-foreground">{progress}</p>}

        {commentIssues.length > 0 && (
          <div role="alert" className="rounded-md border border-destructive/40 p-3 text-sm">
            {commentIssues.map((issue, i) => <p key={i}>{issue}</p>)}
          </div>
        )}

        {result && result.top.length > 0 && (
          <div className="space-y-3">
            <div className="space-y-2 rounded-md border p-3">
              <p className="text-sm font-medium">
                {result.manual
                  ? `Тем в отчёте: ${result.top.length}${result.total > result.top.length ? ` из ${result.total}` : ''}`
                  : `Посевов нашлось: ${result.total}${result.total > result.top.length ? `, в отчёт пойдут ${result.top.length}` : ''}`}
                {period ? ` · ${format(period.from, 'dd.MM')} — ${format(period.to, 'dd.MM.yyyy')}` : ''}
              </p>
              <ol className="space-y-1.5 text-xs">
                {result.top.map((group, i) => (
                  <li key={group.items[0].shortcode} className="flex gap-2">
                    <span className="text-muted-foreground">{i + 1}.</span>
                    <span className="min-w-0 flex-1">
                      <span className={group.headline ? '' : 'text-destructive'}>
                        {group.headline || 'Без подписи — тему впишите в файле'}
                      </span>
                      <span className="mt-0.5 block text-muted-foreground">
                        {group.items.length} {plural(group.items.length, 'ссылка', 'ссылки', 'ссылок')} ·{' '}
                        {group.items.map((i2) => i2.username).join(', ')} · {exactRu(group.totals.views)}{' '}
                        {plural(group.totals.views, 'просмотр', 'просмотра', 'просмотров')}
                      </span>
                      {group.comments && (
                        <span className="mt-1 block space-y-0.5">
                          {group.comments.length === 0 ? (
                            <span className="block text-muted-foreground">Нет комментариев, прошедших отбор</span>
                          ) : (
                            group.comments.map((c) => (
                              <span key={c.id} className="block py-1">
                                {cards.get(c.id) ? (
                                  <img
                                    src={cards.get(c.id)!.dataUrl}
                                    width={cards.get(c.id)!.width}
                                    height={cards.get(c.id)!.height}
                                    alt={`${c.username}: ${c.text}. Лайки: ${c.likeCount}`}
                                    className="max-w-full h-auto"
                                  />
                                ) : (
                                  <span className="block">
                                    {c.username}: {c.text} · ♡ {c.likeCount}
                                    <span className="block text-destructive">Карточка не нарисовалась — в документе будет текст.</span>
                                  </span>
                                )}
                                <button
                                  type="button"
                                  className="text-muted-foreground underline disabled:opacity-50"
                                  disabled={busy}
                                  onClick={() => setResult((current) => current && ({
                                    ...current,
                                    top: current.top.map((g) => g === group
                                      ? { ...g, comments: g.comments?.filter((comment) => comment.id !== c.id) }
                                      : g),
                                  }))}
                                >Убрать комментарий</button>
                              </span>
                            ))
                          )}
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            <ExportMenu
              rememberKey="report:seeding"
              label="Выгрузить"
              formats={[FORMAT_GDOC, FORMAT_DOCX]}
              onSelect={(target) => (target === 'gdoc' ? handleGoogleDoc() : handleDownload())}
              disabled={busy}
              busy={isExporting}
              busyLabel={progress || 'Собираю документ...'}
              className="w-full"
            />
          </div>
        )}

        {result && result.noStats.length > 0 && (
          <div className="space-y-1 rounded-md border p-3">
            <p className="text-sm font-medium">В отчёте, но без статистики — суммы занижены:</p>
            <ul className="space-y-1 text-xs">
              {result.noStats.map((f) => (
                <li key={f.url} className="break-all">
                  <span className="text-muted-foreground">{f.url}</span> — {f.error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {result && result.errors.length > 0 && (
          <div className="space-y-1 rounded-md border border-destructive/40 p-3">
            <p className="text-sm font-medium">Не попали в отчёт:</p>
            <ul className="space-y-1 text-xs">
              {result.errors.map((f) => (
                <li key={f.url} className="break-all">
                  <span className="text-muted-foreground">{f.url}</span> — {f.error}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
